import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as _ from 'lodash';
import { EFormTab } from '../e-form-tab.model';
import { EFormControl } from '../e-form-control.model';
import { Lookup } from '../../lookup/lookup.model';
import { EFormPage } from '../e-form-page.model';
import { EFormColumn } from '../e-form-column.model';
import { EFormRow } from '../e-form-row.model';
import { EFormMergeField } from '../e-form-merge-field.model';
import { EFormService } from '../e-form.service';
import { EFormDataObject } from '../e-formDataObject.model';
import { CSTField } from '../../../shared-modules/customization-framework/models/custom-data-field.model';
import { EFormSurveyField } from '../e-form-survey-field.model';
import { CustomFieldControlTypeEnum } from '../../../shared-modules/customization-framework/models/custom-data-control-type.model';
import { EFormConfiguration } from '../e-form-config.model';


@Injectable()
export class EFormDesignerService {
  masterControlList: EFormControl[] = [];
  promptForDataTypes = ['buttons', 'checkbox', 'checkboxWithTextbox', 'dropdown', 'note', 'radioButtons', 'textbox'];
  staffRoles: Lookup[] = [];
  studentPortfolioCategories: Lookup[] = [];
  documentTypes: Lookup[] = [];
  availableCSTFields: CSTField[] = [];

  private _selectedTab: EFormTab;
  get selectedTab(): EFormTab {
    return this._selectedTab;
  }
  private _selectedPage: EFormPage;
  get selectedPage(): EFormPage {
    return this._selectedPage;
  }
  private _selectedColumn: EFormColumn;
  get selectedColumn(): EFormColumn {
    return this._selectedColumn;
  }
  private _selectedRow: EFormRow;
  get selectedRow(): EFormRow {
    return this._selectedRow;
  }
  private _selectedControl: EFormControl;
  get selectedControl(): EFormControl {
    return this._selectedControl;
  }
  private _selectedElementType: string;
  get selectedElementType(): string {
    return this._selectedElementType;
  }

  locationToAdd: string;
  showSliderPanel: boolean;


  public observableselectedElementChanged = new Subject<boolean>();
  observableselectedElementChangedStream$ = this.observableselectedElementChanged.asObservable();

  connectedDropLists: string[] = [];
  public observableConnectedDropLists = new Subject<string[]>();
  observableConnectedDropListsStream$ = this.observableConnectedDropLists.asObservable();

  public observableTabNameChanged = new Subject<boolean>();
  tabNameChangedEvent = this.observableTabNameChanged.asObservable();

  public observablePagesChanged = new Subject<boolean>();
  pagesChangedEvent = this.observablePagesChanged.asObservable();

  public observableDragStart = new Subject<boolean>();
  observableDragStartStream$ = this.observableDragStart.asObservable();

  public observableDragEnd = new Subject<boolean>();
  observableDragEndStream$ = this.observableDragEnd.asObservable();

  public observableControlsUpdated = new Subject<boolean>();
  observableControlsUpdatedStream$ = this.observableControlsUpdated.asObservable();

  public observableRadioOptionsChanged = new Subject<boolean>();
  observableRadioOptionsChangedStream$ = this.observableRadioOptionsChanged.asObservable();

  public observableMergeFieldClicked = new Subject<EFormMergeField>();
  observableMergeFieldClickedStream$ = this.observableMergeFieldClicked.asObservable();

  public observableChoiceMergeFieldClicked = new Subject<EFormMergeField>();
  observableChoiceMergeFieldClickedStream$ = this.observableChoiceMergeFieldClicked.asObservable();

  public observableCloseMergeFieldWindow = new Subject<boolean>();
  observableCloseMergeFieldWindowStream$ = this.observableCloseMergeFieldWindow.asObservable();

  public observableOpenPropertiesTab = new Subject<boolean>();
  observableOpenPropertiesTabStream$ = this.observableOpenPropertiesTab.asObservable();

  public observableOpenControlTab = new Subject<boolean>();
  observableOpenControlTabStream$ = this.observableOpenControlTab.asObservable();

  public observableshowSliderPanel = new Subject<boolean>();
  observableshowSliderPanelStream$ = this.observableshowSliderPanel.asObservable();

  public observableshowSliderPanelWidth = new Subject<string>();
  observableshowSliderPanelWidthStream$ = this.observableshowSliderPanelWidth.asObservable();

  public observableSelectedPageChanged = new Subject<boolean>();
  observableSelectedPageChangedStream$ = this.observableSelectedPageChanged.asObservable();

  constructor(private eFormService: EFormService) { }

  dragStart() {
    this.observableDragStart.next(true);
  }

  dragEnd() {
    this.observableDragEnd.next(true);
  }

  setUpCriticalChoices(surveyFields: EFormSurveyField[]) {
    surveyFields.forEach(sf => {
      if (sf.CriticalQuestion) {
        const control = this.masterControlList.find(o => o.SurveyFieldID == sf.ID);
        if (control) {
          sf.CriticalValues.forEach(val => {
            const opt = control.DropdownOptions.find(o => o.CodeDesc == val.Value);
            if (opt) {
              opt.IsCritical = true;
            }
          });
        }
      }
    });
  }

  setUpForm(config: EFormConfiguration) {
    const tabIds: number[] = [0];
    const pageIds: number[] = [0];
    if (!this.eFormService.selectedFormData) {
      this.eFormService.selectedFormData = new EFormDataObject();
    }
    if (!this.eFormService.selectedFormData.Tabs || this.eFormService.selectedFormData.Tabs.length == 0) { // set up tabs
      this.eFormService.selectedFormData.Tabs = [];
      const tab = new EFormTab();
      tab.Name = 'Tab 1';
      this.eFormService.selectedFormData.Tabs.push(tab);
    }
    this.eFormService.selectedFormData.Tabs.forEach(tab => { // set up pages
      if (tab.Id) {
        tabIds.push(tab.Id);
      }
      if (!tab.Pages || tab.Pages.length == 0) {
        tab.Pages = [];
        const page = new EFormPage();
        page.Name = 'Page 1';
        tab.Pages.push(page);
      }
      tab.Pages.forEach(page => { // make sure pages have at least one column
        if (page.Id) {
          pageIds.push(page.Id);
        }
        if (!page.Columns || page.Columns.length == 0) {
          page.Columns = [];
          const column = new EFormColumn(config);
          // column.Id = this.getMaxColumnId() + 1;
          page.Columns.push(column);
        }
      });
    });
    this.getAllFormControls();
    if (!this.eFormService.selectedFormData.LastUsedControlId) {
      this.eFormService.selectedFormData.LastUsedControlId = this.getMaxControlId();
    }
    // we were not previously creating ids for tabs and pages, so we need to sync this for older forms.
    let maxTabId = Math.max(...tabIds);
    let maxPageId = Math.max(...pageIds);
    if (this.eFormService.selectedFormData.LastUsedTabId && this.eFormService.selectedFormData.LastUsedTabId > maxTabId) {
      maxTabId = this.eFormService.selectedFormData.LastUsedTabId;
    }
    if (this.eFormService.selectedFormData.LastUsedPageId && this.eFormService.selectedFormData.LastUsedPageId > maxPageId) {
      maxPageId = this.eFormService.selectedFormData.LastUsedPageId;
    }
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      if (!tab.Id) {
        tab.Id = ++maxTabId;
      }
      tab.Pages.forEach(page => {
        if (!page.Id) {
          page.Id = ++maxPageId;
        }
      });
    });
    this.eFormService.selectedFormData.LastUsedTabId = maxTabId;
    this.eFormService.selectedFormData.LastUsedPageId = maxPageId;

  }

  getMaxControlId(): number {
    this.getAllFormControls();
    const control = _.maxBy(this.masterControlList, o => o.Id);
    if (control) {
      return control.Id;
    }
    else {
      return 0;
    }
  }

  getAllFormControls() {
    let controls: EFormControl[] = [];
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      tab.Pages.forEach(page => {
        page.Columns.forEach(column => {
          const colControls = this.getAllControlsByColumn(column);
          if (colControls.length > 0) {
            controls = controls.concat(colControls);
          }
        });
      });
    });

    this.masterControlList = controls;
    this.observableControlsUpdated.next(true);
  }

  getAllControlsByColumn(column: EFormColumn): EFormControl[] {
    let controls: EFormControl[] = [];
    if (column.Controls.length > 0) {
      controls = controls.concat(column.Controls);
    }
    column.Rows.forEach(row => {
      if (row.Controls.length > 0) {
        controls = controls.concat(row.Controls);
      }
      row.Columns.forEach(col => {
        const colControls = this.getAllControlsByColumn(col);
        if (colControls.length > 0) {
          controls = controls.concat(colControls);
        }
      });
    });
    return controls;
  }

  getAllControlsByRow(row: EFormRow): EFormControl[] {
    let controls: EFormControl[] = [];
    if (row.Controls.length > 0) {
      controls = controls.concat(row.Controls);
    }
    row.Columns.forEach(column => {
      if (column.Controls.length > 0) {
        controls = controls.concat(column.Controls);
      }
      column.Rows.forEach(r => {
        const rowControls = this.getAllControlsByRow(r);
        if (rowControls.length > 0) {
          controls = controls.concat(rowControls);
        }
      });
    });
    return controls;
  }

  assignControlsNewID(controls: EFormControl[]) {
    const maxId = this.getMaxControlId();

    for (let i = 1; i <= controls.length; i++) {
      controls[i - 1].Id = maxId + i;
    }
  }

  addRow(isMobile: boolean) {
    if (this.selectedColumn) {
      if (!this.selectedColumn.Rows || this.selectedColumn.Rows.length == 0) {
        this.selectedColumn.Rows = [];
        if (this.selectedColumn.Controls && this.selectedColumn.Controls.length > 0) {
          if (isMobile) {
            // mobile forms can only have one column
            this.selectedColumn.Controls.forEach(cnt => {
              const newRow = new EFormRow();
              newRow.Controls = [];
              newRow.Controls.push(cnt);
              this.selectedColumn.Rows.push(newRow);
            });
          }
          else {
            const newRow = new EFormRow();
            const pnl = _.cloneDeep(this.selectedColumn);
            newRow.Columns.push(pnl);
            this.selectedColumn.Rows.push(newRow);
          }
          this.selectedColumn.Controls = [];
        }
      }
      // ###########################################################################################
      // ###### Click on Copy icon of Row and select posission above/below copy operation ########
      // ###########################################################################################
      if (this.locationToAdd && this.locationToAdd != '') {
        const currentRowIndex = this.selectedColumn.Rows.findIndex(o => o == this.selectedRow);
        if (currentRowIndex >= 0) {
          const rowObj: EFormRow = _.cloneDeep(this.selectedColumn.Rows[currentRowIndex]);
          this.assignControlsNewID(this.getAllControlsByRow(rowObj));

          if (this.locationToAdd == 'above') {
            this.selectedColumn.Rows.splice(currentRowIndex, 0, _.cloneDeep(rowObj));
            // ---- This code for just set focus on create or copy new row
            setTimeout(() => {
              this.focusOnRow(this.selectedColumn.Rows[currentRowIndex]);
            }, 500);
          }
          else if (this.locationToAdd == 'below') {
            this.selectedColumn.Rows.splice(currentRowIndex + 1, 0, _.cloneDeep(rowObj));
            // ---- This code for just set focus on create or copy new row
            setTimeout(() => {
              this.focusOnRow(this.selectedColumn.Rows[currentRowIndex + 1]);
            }, 500);
          }
          this.getAllFormControls();
        }
        this.locationToAdd = '';
      }
      else {
        this.locationToAdd = '';
        // ###################################################################################################
        this.selectedColumn.Rows.push(new EFormRow());
      }
    }
  }

  addColumn(config: EFormConfiguration) {
    if ((this.selectedElementType == 'tab' || this.selectedElementType == 'page') && this.selectedPage) {
      if (!this.selectedPage.Columns) {
        this.selectedPage.Columns = [];
      }
      this.selectedPage.Columns.push(new EFormColumn(config));
    }
    else if (this.selectedElementType == 'row' && this.selectedRow) {
      if (!this.selectedRow.Columns || this.selectedRow.Columns.length == 0) {
        this.selectedRow.Columns = [];
        if (this.selectedRow.Controls && this.selectedRow.Controls.length > 0) {
          const newCol = new EFormColumn(config);
          const row = _.cloneDeep(this.selectedRow);
          newCol.Rows.push(row);
          this.selectedRow.Columns.push(newCol);
          this.selectedRow.Controls = [];
        }
      }
      this.selectedRow.Columns.push(new EFormColumn(config));
    }
    else if (this.selectedElementType == 'column' && this.selectedColumn && this.selectedPage) {

      // ###########################################################################################
      // ###### Click on Copy icon of column and select posission left/right copy operation ########
      // ###########################################################################################
      if (this.locationToAdd && this.locationToAdd != '') {
        let currentColumnIndex = -1;
        if (this.selectedRow) {
          currentColumnIndex = this.selectedRow.Columns.findIndex(o => o == this.selectedColumn);
        }
        if (currentColumnIndex >= 0) {
          const columnObj: EFormColumn = _.cloneDeep(this.selectedRow.Columns[currentColumnIndex]);
          this.assignControlsNewID(this.getAllControlsByColumn(columnObj));
          if (this.locationToAdd == 'left') {
            this.selectedRow.Columns.splice(currentColumnIndex, 0, _.cloneDeep(columnObj));
            // ---- This code for just set focus on create or copy new column
            this.focusOnColumn(this.selectedRow.Columns[currentColumnIndex]);
            // ----
          }
          else if (this.locationToAdd == 'right') {
            this.selectedRow.Columns.splice(currentColumnIndex + 1, 0, _.cloneDeep(columnObj));
            // ---- This code for just set focus on create or copy new column
            this.focusOnColumn(this.selectedRow.Columns[currentColumnIndex + 1]);
            // ----
          }
          this.getAllFormControls();
        }
        else {
          currentColumnIndex = this.selectedPage.Columns.findIndex(o => o == this.selectedColumn);
          if (currentColumnIndex >= 0) {
            const columnObj: EFormColumn = _.cloneDeep(this.selectedPage.Columns[currentColumnIndex]);
            this.assignControlsNewID(this.getAllControlsByColumn(columnObj));
            if (this.locationToAdd == 'left') {
              this.selectedPage.Columns.splice(currentColumnIndex, 0, _.cloneDeep(columnObj));
              // ---- This code for just set focus on create or copy new column
              this.focusOnColumn(this.selectedPage.Columns[currentColumnIndex]);
              // ----
            }
            else if (this.locationToAdd == 'right') {
              this.selectedPage.Columns.splice(currentColumnIndex + 1, 0, _.cloneDeep(columnObj));
              // ---- This code for just set focus on create or copy new column
              this.focusOnColumn(this.selectedPage.Columns[currentColumnIndex + 1]);
              // ----
            }
            this.getAllFormControls();
          }
        }
        this.locationToAdd = '';
      }
      else {
        this.locationToAdd = '';
        // ###################################################################################################

        let parentIsPage = false;
        this.selectedPage.Columns.forEach(col => {
          if (col == this.selectedColumn) {
            parentIsPage = true;
          }
        });
        if (parentIsPage) {
          if (!this.selectedPage.Columns) {
            this.selectedPage.Columns = [];
          }
          this.selectedPage.Columns.push(new EFormColumn(config));
        }
        else {
          this.selectedPage.Columns.forEach(col => {
            const parentRow = this.findParentRowForColumn(col, this.selectedColumn);
            if (parentRow) {
              parentRow.Columns.push(new EFormColumn(config));
            }
          });
        }
      }
    }
    else if (this.selectedElementType == 'control' && this.selectedControl && this.selectedPage) {
      let parentIsPage = false;
      this.selectedPage.Columns.forEach(col => {
        if (col == this.selectedColumn) {
          parentIsPage = true;
        }
      });
      if (parentIsPage) {
        if (!this.selectedPage.Columns) {
          this.selectedPage.Columns = [];
        }
        this.selectedPage.Columns.push(new EFormColumn(config));
      }
      else {
        this.selectedPage.Columns.forEach(col => {
          const parentRow = this.findParentRowForControl(col, this.selectedControl);
          if (parentRow) {
            parentRow.Columns.push(new EFormColumn(config));
          }
        });
      }
    }
    this.locationToAdd = '';
  }

  addControl(config: EFormConfiguration) {
    if (this.selectedColumn) {
      const newControl = new EFormControl();
      newControl.ControlType = this.eFormService.controlTypes.textbox;
      newControl.DataType = 'text';
      newControl.Label = 'Textbox';
      if (this.selectedElementType == 'column') {
        if (this.selectedColumn.Rows && this.selectedColumn.Rows.length > 0) {
          const newRow = new EFormRow();
          const newColumn = new EFormColumn(config);
          newColumn.Controls.push(newControl);
          newRow.Columns.push(newColumn);
          this.selectedColumn.Rows.push(newRow);
        }
        else {
          this.selectedColumn.Controls.push(newControl);
        }
      }
      else if (this.selectedElementType == 'row') {
        const newColumn = new EFormColumn(config);
        newColumn.Controls.push(newControl);
        this.selectedRow.Columns.push(newColumn);
      }
    }
  }

  addTab(config: EFormConfiguration) {
    const newTab = new EFormTab();
    let tabname = 'Tab ' + (this.eFormService.selectedFormData.Tabs.length + 1);
    while (this.eFormService.selectedFormData.Tabs.find(o => o.Name == tabname) != null) {
      tabname += '(1)';
    }
    newTab.Name = tabname;
    newTab.Id = ++this.eFormService.selectedFormData.LastUsedTabId;
    const newPage = new EFormPage();
    newPage.Name = 'Page 1';
    newPage.Id = ++this.eFormService.selectedFormData.LastUsedPageId;
    const newColumn = new EFormColumn(config);
    newPage.Columns = [];
    newPage.Columns.push(newColumn);
    newTab.Pages = [];
    newTab.Pages.push(newPage);
    this.eFormService.selectedFormData.Tabs.push(newTab);
    this.observableTabNameChanged.next(true);
  }

  addPage(config: EFormConfiguration) {
    if (this.selectedTab) {
      const newPage = new EFormPage();
      newPage.Id = ++this.eFormService.selectedFormData.LastUsedPageId;
      let pageName = 'Page ' + (this.selectedTab.Pages.length + 1);
      while (this.selectedTab.Pages.find(o => o.Name == pageName) != null) {
        pageName += '(1)';
      }
      newPage.Name = pageName;
      newPage.Columns = [];
      const newColumn = new EFormColumn(config);
      newPage.Columns.push(newColumn);
      this.selectedTab.Pages.push(newPage);
      this.observablePagesChanged.next(true);
    }
  }

  deleteSelectedElement() {
    switch (this.selectedElementType) {
      case 'tab': {
        const currentTabIndex = this.eFormService.selectedFormData.Tabs.findIndex(o => o == this.selectedTab);
        if (currentTabIndex >= 0) {
          this.eFormService.selectedFormData.Tabs.splice(currentTabIndex, 1);
          if (this.eFormService.selectedFormData.Tabs.length > 0) {
            this.focusOnTab(this.eFormService.selectedFormData.Tabs[0]);
          }
          this.observableTabNameChanged.next(true);
        }
        break;
      }
      case 'page': {
        const currentPageIndex = this.selectedTab.Pages.findIndex(o => o == this.selectedPage);
        if (currentPageIndex >= 0) {
          this.selectedTab.Pages.splice(currentPageIndex, 1);
          if (this.selectedTab.Pages.length > 0) {
            this.focusOnPage(this.selectedTab.Pages[0]);
          }
          else {
            this._selectedPage = null;
          }
        }
        break;
      }
      case 'column': {
        let currentColumnIndex = -1;
        if (this.selectedRow) {
          currentColumnIndex = this.selectedRow.Columns.findIndex(o => o == this.selectedColumn);
        }
        if (currentColumnIndex >= 0) {
          this.selectedRow.Columns.splice(currentColumnIndex, 1);
          this._selectedColumn = null;
        }
        else {
          currentColumnIndex = this.selectedPage.Columns.findIndex(o => o == this.selectedColumn);
          if (currentColumnIndex >= 0) {
            this.selectedPage.Columns.splice(currentColumnIndex, 1);
            this._selectedColumn = null;
          }
        }
        break;
      }
      case 'row': {
        const currentRowIndex = this.selectedColumn.Rows.findIndex(o => o == this.selectedRow);
        if (currentRowIndex >= 0) {
          this.selectedColumn.Rows.splice(currentRowIndex, 1);
          this._selectedRow = null;
        }
        break;
      }
      case 'control': {
        let currentControlIndex = this.selectedColumn.Controls.findIndex(o => o == this.selectedControl);
        if (currentControlIndex >= 0) {
          this.selectedColumn.Controls.splice(currentControlIndex, 1);
          this._selectedControl = null;
        }
        else {
          currentControlIndex = this.selectedRow.Controls.findIndex(o => o == this.selectedControl);
          if (currentControlIndex >= 0) {
            this.selectedRow.Controls.splice(currentControlIndex, 1);
            this._selectedControl = null;
          }
        }

        if (this.selectedTab.Pages.length > 0) {
          this.focusOnPage(this.selectedTab.Pages[0]);
        }
        else if (this.eFormService.selectedFormData.Tabs.length > 0) {
          this.focusOnTab(this.eFormService.selectedFormData.Tabs[0]);
        }

        this.getAllFormControls();
        break;
      }
    }
    this.openControlTab();
  }

  findParentRowForColumn(container: EFormColumn, elementToFind: EFormColumn): EFormRow {
    let parentRow: EFormRow = null;
    container.Rows.forEach(row => {
      row.Columns.forEach(column => {
        if (column == elementToFind) {
          parentRow = row;
        }
      });
      row.Columns.forEach(column => {
        if (!parentRow) {
          parentRow = this.findParentRowForColumn(column, elementToFind);
        }
      });
    });
    return parentRow;
  }

  findParentRowForControl(container: EFormColumn, elementToFind: EFormControl): EFormRow {
    let parentRow: EFormRow = null;
    container.Rows.forEach(row => {
      row.Controls.forEach(control => {
        if (control == elementToFind) {
          parentRow = row;
        }
      });
      row.Columns.forEach(column => {
        column.Controls.forEach(control => {
          if (control == elementToFind) {
            parentRow = row;
          }
        });
        if (!parentRow) {
          parentRow = this.findParentRowForControl(column, elementToFind);
        }
      });
    });
    return parentRow;
  }

  findParentColumnForRow(container: EFormColumn, elementToFind: EFormRow): EFormColumn {
    let parentColumn: EFormColumn = null;
    container.Rows.forEach(row => {
      if (row == elementToFind) {
        parentColumn = container;
      }
      row.Columns.forEach(column => {
        if (!parentColumn) {
          parentColumn = this.findParentColumnForRow(column, elementToFind);
        }
      });
    });
    return parentColumn;
  }

  findParentColumnForControl(container: EFormColumn, elementToFind: EFormControl): EFormColumn {
    let parentColumn: EFormColumn = null;
    container.Controls.forEach(control => {
      if (control == elementToFind) {
        parentColumn = container;
      }
    });
    container.Rows.forEach(row => {
      row.Controls.forEach(control => {
        if (control == elementToFind) {
          parentColumn = container;
        }
      });
      row.Columns.forEach(column => {
        if (!parentColumn) {
          parentColumn = this.findParentColumnForControl(column, elementToFind);
        }
      });
    });
    return parentColumn;
  }

  isControlOnPage(control: EFormControl, page: EFormPage): boolean {
    let controlFound = false;
    page.Columns.forEach(col => {
      if (!controlFound && this.findParentColumnForControl(col, control) != null) {
        controlFound = true;
      }
    });
    return controlFound;
  }

  isColumnOnPage(column: EFormColumn, page: EFormPage): boolean {
    let controlFound = false;
    page.Columns.forEach(col => {
      if (col == column) {
        controlFound = true;
      }
      else if (controlFound == false && this.findParentRowForColumn(col, column) != null) {
        controlFound = true;
      }
    });
    return controlFound;
  }

  isRowOnPage(row: EFormRow, page: EFormPage): boolean {
    let controlFound = false;
    page.Columns.forEach(col => {
      if (controlFound == false && this.findParentColumnForRow(col, row) != null) {
        controlFound = true;
      }
    });
    return controlFound;
  }

  moveElement(direction: string) {
    if (this.selectedColumn && this.selectedRow) {
      if (direction == 'up') {
        let index = this.selectedColumn.Rows.findIndex(o => o == this.selectedRow);
        if (index > 0) {
          this.selectedColumn.Rows.splice(index, 1);
          index--;
          this.selectedColumn.Rows.splice(index, 0, this.selectedRow);
        }
      }
      else if (direction == 'down') {
        const maxIndex = this.selectedColumn.Rows.length - 1;
        let index = this.selectedColumn.Rows.findIndex(o => o == this.selectedRow);
        if (index >= 0 && index < maxIndex) {
          this.selectedColumn.Rows.splice(index, 1);
          index++;
          this.selectedColumn.Rows.splice(index, 0, this.selectedRow);
        }
      }
      else if (direction == 'left') {
        let index = this.selectedRow.Columns.findIndex(o => o == this.selectedColumn);
        if (index > 0) {
          this.selectedRow.Columns.splice(index, 1);
          index--;
          this.selectedRow.Columns.splice(index, 0, this.selectedColumn);
        }
      }
      else if (direction == 'right') {
        const maxIndex = this.selectedRow.Columns.length - 1;
        let index = this.selectedRow.Columns.findIndex(o => o == this.selectedColumn);
        if (index >= 0 && index < maxIndex) {
          this.selectedRow.Columns.splice(index, 1);
          index++;
          this.selectedRow.Columns.splice(index, 0, this.selectedColumn);
        }
      }
    }
  }

  connectedDropListAdd(id: string) {
    if (this.connectedDropLists.find(o => o == id) == null) {
      this.connectedDropLists.push(id);
      this.observableConnectedDropLists.next(this.connectedDropLists);
    }
  }

  getPromptForDataObjects(currentControl: EFormControl, page: boolean = false): EFormControl[] {
    const promptForDataObjects: EFormControl[] = [];
    if (currentControl || page) {
      for (let i = 0; i < this.masterControlList.length; i++) {
        const control = this.masterControlList[i];
        if (!page && control.Id == currentControl.Id) {
          break;
        }
        if (this.promptForDataTypes.findIndex(o => o == control.ControlType) >= 0) {
          promptForDataObjects.push(control);
        }
      }
    }
    return promptForDataObjects;
  }

  getPagePromptForDataObjects(page: EFormPage): EFormControl[] {
    if (page.Columns && page.Columns.length > 0) {
      return this.getPromptForDataObjects(null, true);
    }
    return [];
  }

  getTabPromptForDataObjects(tab: EFormTab): EFormControl[] {
    if (tab.Pages && tab.Pages.length > 0) {
      return this.getPagePromptForDataObjects(tab.Pages[0]);
    }
    return [];
  }

  focusOnTab(tab: EFormTab) {
    this._selectedTab = tab;
    this._selectedElementType = 'tab';
    if (tab.Pages && tab.Pages.length > 0) {
      this._selectedPage = tab.Pages[0];
    }
    else {
      this._selectedPage = null;
    }
    this.selectedElementChanged();
    this.observableSelectedPageChanged.next(true);
  }

  focusOnPage(page: EFormPage) {
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      if (tab.Pages.find(o => o == page)) {
        this._selectedTab = tab;
      }
    });
    this._selectedPage = page;
    this._selectedElementType = 'page';
    this.selectedElementChanged();
    this.observableSelectedPageChanged.next(true);
  }

  focusOnColumn(col: EFormColumn) {
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      tab.Pages.forEach(page => {
        if (this.isColumnOnPage(col, page)) {
          this._selectedTab = tab;
          this._selectedPage = page;
          this._selectedRow = null;
          page.Columns.forEach(pageCol => {
            const row = this.findParentRowForColumn(pageCol, col);
            if (row) {
              this._selectedRow = row;
            }
          });
          this._selectedColumn = col;
          this._selectedElementType = 'column';
          this.selectedElementChanged();
        }
      });
    });
  }

  focusOnRow(row: EFormRow) {
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      tab.Pages.forEach(page => {
        if (this.isRowOnPage(row, page)) {
          this._selectedTab = tab;
          this._selectedPage = page;
          this._selectedColumn = null;
          page.Columns.forEach(pageCol => {
            const col = this.findParentColumnForRow(pageCol, row);
            if (col) {
              this._selectedColumn = col;
            }
          });
          this._selectedRow = row;
          this._selectedElementType = 'row';
          this.selectedElementChanged();
        }
      });
    });
  }

  focusOnControl(control: EFormControl) {
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      tab.Pages.forEach(page => {
        if (this.isControlOnPage(control, page)) {
          if (tab != this.selectedTab) {
            this._selectedTab = tab;
          }
          if (page != this.selectedPage) {
            this._selectedPage = page;
          }
          this._selectedColumn = null;
          page.Columns.forEach(pageCol => {
            const col = this.findParentColumnForControl(pageCol, control);
            if (col) {
              this._selectedColumn = col;
            }
          });
          this._selectedRow = null;
          page.Columns.forEach(pageCol => {
            const row = this.findParentRowForControl(pageCol, control);
            if (row) {
              this._selectedRow = row;
            }
          });
          if (control != this.selectedControl) {
            this._selectedControl = control;
          }
          this._selectedElementType = 'control';
          this.selectedElementChanged();
        }
      });
    });
  }

  selectedElementChanged() {
    this.observableselectedElementChanged.next(true);
  }

  openPropertiesTab() {
    this.observableOpenPropertiesTab.next(true);
  }

  openControlTab() {
    this.observableOpenControlTab.next(true);
  }

  treeViewExpandAll(expanded: boolean) {
    if (this.eFormService.selectedFormData) {
      this.eFormService.selectedFormData.Tabs.forEach(tab => {
        tab.Expanded = expanded;
        tab.Pages.forEach(page => {
          page.Expanded = expanded;
          page.Columns.forEach(col => {
            this.setExpandAllByColumn(col, expanded);
          });
        });
      });
    }
  }

  setExpandAllByColumn(col: EFormColumn, expanded: boolean) {
    col.Expanded = expanded;
    col.Rows.forEach(row => {
      row.Expanded = expanded;
      row.Columns.forEach(rowCol => {
        this.setExpandAllByColumn(rowCol, expanded);
      });
    });
  }

  closeMergeFieldWindow() {
    this.observableCloseMergeFieldWindow.next(true);
  }

  updateConditionalControls(control: EFormControl) {
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      if (tab.ParentObjectId && tab.ParentObjectId == control.Id) {
        const index: number = control.DropdownOptions.findIndex(ddo => tab.Condition && ddo.Code == tab.Condition);
        if (index == -1) {
          tab.Condition = control.DropdownOptions[0].Code;
        }
      }

      tab.Pages.forEach(page => {
        if (page.ParentObjectId && page.ParentObjectId == control.Id) {
          const index: number = control.DropdownOptions.findIndex(ddo => page.Condition && ddo.Code == page.Condition);
          if (index == -1) {
            page.Condition = control.DropdownOptions[0].Code;
          }
        }
      });

      this.eFormService.allFormControls.forEach( formControl => {
        if (formControl.ParentObjectId && formControl.ParentObjectId == control.Id) {
          const index: number = control.DropdownOptions.findIndex(ddo => formControl.Condition && ddo.Code == formControl.Condition);
          if (index == -1) {
            formControl.Condition = control.DropdownOptions[0].Code;
          }
        }
      });
    });
  }

  rowHasNonRemovableControls(row: EFormRow): boolean {
    let nonRemovable = false;
    if (row.Controls && row.Controls.length > 0) {
      nonRemovable = row.Controls.find(o => o.CannotBeRemoved) != null;
    }
    else if (row.Columns && row.Columns.length > 0) {
      row.Columns.forEach(col => {
        if (this.columnHasNonRemovableControls(col)) {
          nonRemovable = true;
        }
      });
    }
    return nonRemovable;
  }

  columnHasNonRemovableControls(col: EFormColumn): boolean {
    let nonRemovable = false;
    if (col.Controls && col.Controls.length > 0) {
      nonRemovable = col.Controls.find(o => o.CannotBeRemoved) != null;
    }
    else if (col.Rows && col.Rows.length > 0) {
      col.Rows.forEach(row => {
        if (this.rowHasNonRemovableControls(row)) {
          nonRemovable = true;
        }
      });
    }
    return nonRemovable;
  }

  pageHasNonRemovableControls(page: EFormPage): boolean {
    let nonRemovable = false;
    if (page.Columns && page.Columns.length > 0) {
      page.Columns.forEach(col => {
        if (this.columnHasNonRemovableControls(col)) {
          nonRemovable = true;
        }
      });
    }
    else if (page.Rows && page.Rows.length > 0) {
      page.Rows.forEach(row => {
        if (this.rowHasNonRemovableControls(row)) {
          nonRemovable = true;
        }
      });
    }
    return nonRemovable;
  }

  tabHasNonRemovableControls (tab: EFormTab): boolean {
    let nonRemovable = false;
    if (tab.Pages && tab.Pages.length > 0) {
      tab.Pages.forEach(page => {
        if (this.pageHasNonRemovableControls(page)) {
          nonRemovable = true;
        }
      });
    }
    return nonRemovable;
  }

  convertEFormControlTypeToCustomFieldTypeID(ControlType: string): number {
    switch (ControlType) {
      case this.eFormService.controlTypes.textbox:
        return CustomFieldControlTypeEnum.Textbox;
      case this.eFormService.controlTypes.image:
        return CustomFieldControlTypeEnum.Image;
      case this.eFormService.controlTypes.buttons:
        return CustomFieldControlTypeEnum.Buttons;
      case this.eFormService.controlTypes.radioButtons:
        return CustomFieldControlTypeEnum.RadioButtons;
      case this.eFormService.controlTypes.note:
        return CustomFieldControlTypeEnum.MultiLineTextbox;
      case this.eFormService.controlTypes.date:
        return CustomFieldControlTypeEnum.Date;
      case this.eFormService.controlTypes.checkbox:
        return CustomFieldControlTypeEnum.Checkbox;
      case this.eFormService.controlTypes.dropdown:
        return CustomFieldControlTypeEnum.Dropdown;
      case this.eFormService.controlTypes.text:
        return CustomFieldControlTypeEnum.Text;
      case this.eFormService.controlTypes.component:
        return CustomFieldControlTypeEnum.Component;
      case this.eFormService.controlTypes.time:
        return CustomFieldControlTypeEnum.Time;
      default:
        return -1;
    }
  }

  canDeleteColumn(col: EFormColumn): boolean {
    // get page this column is on
    let parentPage: EFormPage;
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      tab.Pages.forEach(page => {
        if (this.isColumnOnPage(col, page)) {
          parentPage = page;
        }
      });
    });
    // cannot delete colum if it is the only top-level column on the page
    return parentPage && col && parentPage.Columns && (parentPage.Columns.length > 1 || parentPage.Columns[0] != col);
  }

  canDeletePage(page: EFormPage): boolean {
    // get tab this page is on
    let parentTab: EFormTab;
    this.eFormService.selectedFormData.Tabs.forEach(tab => {
      tab.Pages.forEach(p => {
        if (p == page) {
          parentTab = tab;
        }
      });
    });
    // cannot delete page if the tab only has one page
    return page && parentTab && parentTab.Pages.length > 1;
  }
}
